<template>
  <base-material-card color="primary" icon="mdi-calculator-variant-outline" inline class="ml-0 px-5 py-4">
    <template v-slot:after-heading>
      <h3>
        Quantitativos da BDGD
        <span>
          {{ bdgd.ordinaria ? 'Ordinária' : '' }}
          {{ bdgd.data_registro | formatToMonth }} v{{ bdgd.versao }}
        </span>
      </h3>
    </template>
    <v-row class="my-8" dense>
      <v-col cols="2" class="mt-2">
        <div class="mb-12">
          <v-autocomplete
            v-model="filters.regionais"
            label="Regional"
            dense
            outlined
            hide-details
            :items="regionais"
            :item-text="(item) => item.nome.toUpperCase()"
            clearable
          ></v-autocomplete>
        </div>

        <div class="mb-8">
          <v-autocomplete
            v-model="filters.municipios"
            dense
            outlined
            label="Município"
            :items="municipios"
            :item-text="(item) => item.nome.toUpperCase()"
            item-value="id"
            :disabled="!filters.regionais"
            :loading="loadingMunicipios"
            clearable
            :hint="!filters.regionais ? 'Selecione regional' : ''"
            :persistent-hint="!filters.regionais"
          ></v-autocomplete>
        </div>

        <div class="mb-10">
          <v-autocomplete
            v-model="filters.conjuntos"
            label="Conjunto Elétrico"
            dense
            outlined
            :items="conjuntos"
            :item-text="(item) => `${item.cod_id} - ${item.nome.toUpperCase()}`"
            item-value="cod_id"
            :loading="loadingConjuntos"
            clearable
          ></v-autocomplete>
        </div>

        <div class="d-flex flex-column flex-md-row gap-4 justify-center mt-4">
          <v-btn small color="primary" dark class="flex-grow-1 mb-2 mb-md-0 me-md-2 py-3" elevation="2" @click="onFiltroChange">
            Aplicar
          </v-btn>
          <v-btn small color="secondary" outlined class="flex-grow-1 py-3" @click="limparFiltros">Limpar</v-btn>
        </div>
      </v-col>

      <v-col cols="10">
        <v-overlay :value="loadingDados" absolute>
          <v-progress-circular indeterminate size="48" color="primary" />
        </v-overlay>
        <v-row dense>
          <v-col v-for="item in items" :key="item.key" cols="12" sm="6" md="3">
            <v-tooltip bottom color="grey">
              <template v-slot:activator="{ on, attrs }">
                <div
                  class="align-center card-item d-flex flex-column"
                  v-bind="attrs"
                  v-on="on"
                  @click="abrirModalInformativos(item)"
                >
                  <v-icon size="48" color="primary" class="my-2">
                    {{ item.icon }}
                  </v-icon>

                  <div class="text-body-2 text-uppercase">
                    {{ item.label }}
                  </div>

                  <div class="font-weight-light my-2 text-h5">
                    {{ formatNumber(dados[item.key]) }}
                  </div>
                </div>
              </template>

              <span>Abrir Informativos</span>
            </v-tooltip>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
    <dialog-base
      v-if="quantitativoSelecionado"
      v-model="modalGraficos"
      :titulo="`Mais informativos - ${quantitativoSelecionado.label}${filtrosAtivosTexto}`"
      @fechar="fecharModalInformativos"
    >
      <v-row dense>
        <v-col sm="6">
          <!-- Mapa da Localização geografica das entidades -->
          <v-card class="ma-0 pa-0" height="100%">
            <div class="font-weight-light ml-4 pt-4">
              Localização Geográfica da Entidade ({{ quantitativoSelecionado.label }})
            </div>
            <v-row>
              <v-col cols="12">
                <mapa-localizacao-geografica :layer="quantitativoSelecionado.key" :filtros-ativos="filters" />
              </v-col>
            </v-row>
          </v-card>
        </v-col>
        <v-col sm="6">
          <!-- Grafico das entidades por regional -->
          <v-card v-if="exibeGraficoPorRegional" class="ma-0 pa-0" height="100%">
            <div class="ml-4 pt-4">Qtd de {{ quantitativoSelecionado.label }} por regional</div>
            <v-row>
              <v-col cols="12">
                <grafico-entidades
                  :entidade="quantitativoSelecionado"
                  :filtros-ativos="filters"
                  tipo="regional"
                ></grafico-entidades>
              </v-col>
            </v-row>
          </v-card>
          <!-- Grafico de postes por material -->
          <v-card v-if="exibeGraficoPorMaterial" class="ma-0 pa-0" height="100%">
            <div class="ml-4 pt-4">Qtd de {{ quantitativoSelecionado.label }} por Material</div>
            <v-row>
              <v-col cols="12">
                <grafico-entidades
                  :entidade="quantitativoSelecionado"
                  :filtros-ativos="filters"
                  tipo="material"
                ></grafico-entidades>
              </v-col>
            </v-row>
          </v-card>
        </v-col>
      </v-row>
      <v-row dense>
        <v-col sm="6">
          <!-- Grafico das entidades por municipio -->
          <v-card class="ma-0 pa-0" height="100%">
            <div class="font-weight-light ml-4 pt-4">Qtd de {{ quantitativoSelecionado.label }} por Município (Top 10)</div>
            <v-row>
              <v-col cols="12">
                <grafico-entidades
                  :entidade="quantitativoSelecionado"
                  :filtros-ativos="filters"
                  tipo="municipio"
                ></grafico-entidades>
              </v-col>
            </v-row>
          </v-card>
        </v-col>
        <v-col sm="6">
          <!-- Tabela por alimentador -->
          <v-card v-if="exibeTabelaAlimentador" class="ma-0 pa-0" height="100%">
            <div class="font-weight-light ml-4 pt-4">Qtd de {{ quantitativoSelecionado.label }} por Alimentador</div>
            <v-row>
              <v-col cols="12">
                <tabela-entidades :entidade="quantitativoSelecionado" :filtros-ativos="filters" tipo="alimentador" />
              </v-col>
            </v-row>
          </v-card>
          <!-- Tabela por potencia e fase -->
          <v-card v-if="exibeTabelaPorPotencia" class="ma-0 pa-0" height="100%">
            <div class="font-weight-light ml-4 pt-4">Qtd de {{ quantitativoSelecionado.label }} por Potência e Fase</div>
            <v-row>
              <v-col cols="12">
                <tabela-entidades :entidade="quantitativoSelecionado" :filtros-ativos="filters" tipo="trafosPorPotencia" />
              </v-col>
            </v-row>
          </v-card>
          <!-- Tabela por localizacao -->
          <v-card v-if="exibeTabelaPorLocalizacao" class="ma-0 pa-0" height="100%">
            <div class="font-weight-light ml-4 pt-4">Qtd de {{ quantitativoSelecionado.label }} por Localização</div>
            <v-row>
              <v-col cols="12">
                <tabela-entidades :entidade="quantitativoSelecionado" :filtros-ativos="filters" tipo="localizacao" />
              </v-col>
            </v-row>
          </v-card>
          <!-- Grafico Por Unidade Consumidora -->
          <v-card v-if="exibeGraficoPorUnidadeConsumidora" class="ma-0 pa-0" height="100%">
            <div class="font-weight-light ml-4 pt-4">Comparativo de Unidades Consumidoras</div>
            <v-row>
              <v-col cols="12">
                <grafico-entidades :entidade="quantitativoSelecionado" :filtros-ativos="filters" tipo="uc" />
              </v-col>
            </v-row>
          </v-card>
          <!-- Grafico Por Tipo de Unidade -->
          <v-card v-if="exibeGraficoPorTipoUnidade" class="ma-0 pa-0" height="100%">
            <div class="font-weight-light ml-4 pt-4">Comparativo de Tipos de Unidades</div>
            <v-row>
              <v-col cols="12">
                <grafico-entidades :entidade="quantitativoSelecionado" :filtros-ativos="filters" tipo="tipounidade" />
              </v-col>
            </v-row>
          </v-card>
        </v-col>
      </v-row>
    </dialog-base>
  </base-material-card>
</template>

<script>
  import DashboardService from '@/services/DashboardService';
  import BdgdVersionsService from '@/services/BdgdVersionsService';
  import MapaLocalizacaoGeografica from '../../geracao-bdgd/validacoes-bdgd/quantitativos-bdgd/MapaLocalizacaoGeografica.vue';
  import DialogBase from '../dialogs/DialogBase.vue';
  import GraficoEntidades from '../../geracao-bdgd/validacoes-bdgd/quantitativos-bdgd/GraficoEntidades.vue';
  import TabelaEntidades from '../../geracao-bdgd/validacoes-bdgd/quantitativos-bdgd/TabelaEntidades.vue';

  export default {
    name: 'DashboardQuantitativo',
    components: {
      MapaLocalizacaoGeografica,
      DialogBase,
      GraficoEntidades,
      TabelaEntidades
    },
    data() {
      return {
        bdgd: {
          id: 0,
          data_registro: null,
          versao: 1,
          ordinaria: false
        },
        modalGraficos: false,
        quantitativoSelecionado: null,
        dados: {
          postes: 0,
          transformadores: 0,
          redeBt: 0,
          redeMt: 0,
          bancoCapacitor: 0,
          ucBt: 0,
          ucMt: 0,
          chaves: 0,
          religador: 0,
          disjuntores: 0,
          regulador: 0,
          torres: 0,
          eqme: 0,
          transformadoresAt: 0
        },

        filters: {
          municipios: null,
          regionais: null,
          conjuntos: null
        },

        loadingMunicipios: false,
        loadingConjuntos: false,
        loadingDados: false,

        municipios: [],
        regionais: [],
        conjuntos: [],

        items: [
          { key: 'postes', icon: 'mdi-post-lamp', label: 'Postes' },
          { key: 'torres', icon: 'mdi-transmission-tower', label: 'Torres' },
          { key: 'transformadores', icon: 'mdi-transit-connection-variant', label: 'Transformadores MT' },
          { key: 'transformadoresAt', icon: 'mdi-chip', label: 'Transformadores AT' },
          { key: 'redeBt', icon: 'mdi-transmission-tower', label: 'Rede BT (km)' },
          { key: 'redeMt', icon: 'mdi-transmission-tower-export', label: 'Rede MT (km)' },
          { key: 'redeAt', icon: 'mdi-transmission-tower-import', label: 'Rede AT (km)' },
          { key: 'bancoCapacitor', icon: 'mdi-fuel-cell', label: 'Banco Capacitor' },
          { key: 'ucBt', icon: 'mdi-home-lightning-bolt', label: 'UCBT' },
          { key: 'ucMt', icon: 'mdi-home-lightning-bolt-outline', label: 'UCMT' },
          { key: 'ucAt', icon: 'mdi-home-lightning-bolt-outline', label: 'UCAT' },
          { key: 'chaves', icon: 'mdi-electric-switch-closed', label: 'Chaves' },
          { key: 'religador', icon: 'mdi-electric-switch', label: 'Religador' },
          { key: 'disjuntores', icon: 'mdi-light-switch-off', label: 'Disjuntores' },
          { key: 'regulador', icon: 'mdi-lightning-bolt-circle', label: 'Regulador' },
          { key: 'eqme', icon: 'mdi-chart-gantt', label: 'Equipamento Medidor' }
        ]
      };
    },

    computed: {
      bdgdVersaoId() {
        const { getUserSelectedCompanyBdgdVersion } = this.$store.getters;
        if (getUserSelectedCompanyBdgdVersion) {
          return getUserSelectedCompanyBdgdVersion.id;
        }
        return null;
      },
      filtrosAtivosTexto() {
        const partes = [];

        if (this.filters.regionais) {
          const regional = this.regionais.find((r) => r === this.filters.regionais || r.id === this.filters.regionais);
          if (regional) {
            partes.push(`Regional: ${regional.nome || regional}`);
          }
        }

        if (this.filters.municipios) {
          const municipio = this.municipios.find((m) => m.id === this.filters.municipios);
          if (municipio) {
            partes.push(`Município: ${municipio.nome}`);
          }
        }

        if (this.filters.conjuntos) {
          const conjunto = this.conjuntos.find((c) => c.cod_id === this.filters.conjuntos);
          if (conjunto) {
            partes.push(`Conjunto: ${conjunto.cod_id}`);
          }
        }

        return partes.length > 0 ? ` - ${partes.join(' - ')}` : '';
      },
      exibeTabelaAlimentador() {
        const keysAlimentador = ['chaves', 'regulador', 'religador', 'disjuntores', 'redeBt', 'redeMt'];
        return keysAlimentador.includes(this.quantitativoSelecionado?.key);
      },
      exibeTabelaPorPotencia() {
        return ['transformadores', 'transformadoresAt'].includes(this.quantitativoSelecionado.key);
      },
      exibeTabelaPorLocalizacao() {
        return ['postes', 'bancoCapacitor', 'torres'].includes(this.quantitativoSelecionado.key);
      },
      exibeGraficoPorRegional() {
        return this.quantitativoSelecionado.key !== 'postes' && this.quantitativoSelecionado.key !== 'torres';
      },
      exibeGraficoPorMaterial() {
        return ['postes', 'torres'].includes(this.quantitativoSelecionado.key);
      },
      exibeGraficoPorUnidadeConsumidora() {
        return ['ucBt', 'ucMt', 'ucAt'].includes(this.quantitativoSelecionado?.key);
      },
      exibeGraficoPorTipoUnidade() {
        return this.quantitativoSelecionado?.key === 'eqme';
      }
    },

    mounted() {
      this.getDados(), this.getFiltros(), this.getConjuntos(), this.getBdgd();
    },
    methods: {
      getBdgd() {
        if (!this.bdgdVersaoId) return;
        BdgdVersionsService.getById(this.bdgdVersaoId)
          .then((response) => (this.bdgd = response))
          .catch((err) => {
            console.log('Erro: ', err);
            this.$toast.error('Erro ao buscar os dados da versão da BDGD selecionada.', '', { position: 'topRight' });
          });
      },
      getDados() {
        this.loadingDados = true;
        DashboardService.getDados('QuantitativosBdgd', {
          regional: this.filters.regionais,
          municipio: this.filters.municipios,
          conjunto: this.filters.conjuntos
        })
          .then((response) => {
            this.dados = response.data;
          })
          .catch((err) => {
            console.log('Erro: ', err);
            this.$toast.error(`Erro ao buscar os dados do painel".`, '', { position: 'topRight' });
          })
          .finally(() => {
            this.loadingDados = false;
          });
      },

      getFiltros() {
        DashboardService.getRegionais()
          .then(({ data }) => {
            this.regionais = data.regionais;
          })
          .catch((err) => {
            console.log('Erro: ', err);
            this.$toast.error(`Erro ao buscar os filtros".`, '', { position: 'topRight' });
          });
      },
      getMunicipios(regional) {
        this.loadingMunicipios = true;

        DashboardService.getMunicipiosPorRegional(regional)
          .then((res) => {
            this.municipios = res.data;
          })
          .finally(() => {
            this.loadingMunicipios = false;
          });
      },
      getConjuntos(regional) {
        this.loadingConjuntos = true;

        DashboardService.getConjuntosPorRegional(regional)
          .then((res) => {
            this.conjuntos = res.data;
          })
          .finally(() => {
            this.loadingConjuntos = false;
          });
      },
      onFiltroChange() {
        if (!this.filters.regionais) {
          this.filters.municipios = null;
        }
        this.getDados();
      },
      limparFiltros() {
        this.filters.regionais = null;
        this.filters.municipios = null;
        this.filters.conjuntos = null;

        this.getDados();
      },
      formatNumber(value) {
        if (value === null || value === undefined) return '0';

        return Number(value).toLocaleString('pt-BR', {
          maximumFractionDigits: 2
        });
      },
      abrirModalInformativos(item) {
        this.quantitativoSelecionado = item;
        this.modalGraficos = true;
      },
      fecharModalInformativos() {
        this.modalGraficos = false;
        this.quantitativoSelecionado = null;
      }
    },
    watch: {
      'filters.regionais': {
        handler(novaRegional, regionalAnterior) {
          this.filters.municipios = null;

          if (novaRegional !== regionalAnterior) {
            this.filters.conjuntos = null;
          }

          if (!novaRegional) {
            this.municipios = [];
            this.conjuntos = this.getConjuntos();
            return;
          }

          this.getMunicipios(novaRegional);
          this.getConjuntos(novaRegional);
        },
        immediate: false
      }
    }
  };
</script>
<style scoped>
  .card-item {
    cursor: pointer;
    border-radius: 10px;
  }
  .card-item:hover {
    background-color: rgb(240, 240, 240);
  }
</style>
